import { keepPreviousData, Updater, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AppID, FlowID } from '@playq/octopus-common';
import { FlowBrief, FlowPromotions, FlowsSort, QueryFlowsResponse } from '@playq/octopus2-liveops';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';
import { PermModule } from '@playq/octopus2-auth';

import { IEntityPromotion } from '/api/hooks/promotions';
import {
  IUseQueryOptionsExtended,
  useEitherQuery,
  useMutateQueryDataAfterEntityDeleting,
  useQueryPrefetch,
} from '/api/service-hooks';
import { services2 } from '/api/services2';
import { QueryFilter } from '/common/models';
import { iteratorMaxLimit } from '/constants';
import { appToolkit } from '/store';
import { useModulePermissions } from '/hooks/useModulePermissions';

import { createFlowsResponseAfterFlowDeleting, mutatePromotionsAndFeaturesAfterFlowDeleting } from './helpers';

const emptyArray: [] = [];

const defaultQuery = {
  iterator: new OffsetLimit({ limit: iteratorMaxLimit, offset: 0 }),
  sortBy: [],
  filterBy: {},
};

export const flowQueryKeys: unknown[] = ['flows', 'service', 'query'];

export const useFlowsQuery = (
  iterator: OffsetLimit,
  sortBy: FlowsSort[] = emptyArray,
  filterBy: QueryFilter = defaultQuery.filterBy,
  options?: IUseQueryOptionsExtended<QueryFlowsResponse | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID);

  const queryClient = useQueryClient();

  const keys = useMemo(
    () => flowQueryKeys.concat(appID, iterator, sortBy, filterBy),
    [appID, iterator, sortBy, filterBy]
  );

  const { canRead } = useModulePermissions(PermModule.LiveOpsFlow);

  const isValid = appID !== undefined && canRead;

  const { mutate, refetch, ...res } = useEitherQuery(
    keys,
    () => services2.flowsService.queryFlows(appID as AppID, iterator, sortBy, filterBy),
    {
      placeholderData: keepPreviousData,
      ...options,
      enabled: options?.enabled !== false && isValid,
    }
  );

  const total = res.data?.total ?? 0;

  const { nextKeys } = useQueryPrefetch({
    keys,
    enabled: options?.enablePrefetch !== false && appID !== undefined,
    total,
    args: [appID as AppID, iterator, sortBy, filterBy],
    serviceMethod: services2.flowsService.queryFlows.bind(services2.flowsService),
  });

  const mutateUpdatePromotions = useCallback(
    (promotions: Updater<IEntityPromotion<FlowID>[], IEntityPromotion<FlowID>[]>) => {
      mutate((prevResponse) => {
        if (!prevResponse) {
          return;
        }
        const response = new QueryFlowsResponse(prevResponse.serialize());
        response.flows = prevResponse.flows;
        response.iterator = prevResponse.iterator;
        response.features = prevResponse.features;

        const nextPromtions = typeof promotions === 'function' ? promotions(response.promotions) : promotions;
        response.promotions = nextPromtions.map(
          (p) => new FlowPromotions({ id: p.id.serialize(), promotions: p.promotions.serialize() })
        );
        return response;
      });
    },
    [mutate]
  );

  const mutateDeletedFlow = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    nextPageQueryKey: nextKeys,
    total,
    iterator,
    mutate,
    refetch,
    getID: (f?: FlowBrief) => f?.id,
    getEntities: (r: QueryFlowsResponse) => r.flows,
    setEntities: (r: QueryFlowsResponse, newEntities: FlowBrief[]) => {
      r.flows = newEntities;
    },
    createNewResponse: createFlowsResponseAfterFlowDeleting,
    mutateCustomFields: mutatePromotionsAndFeaturesAfterFlowDeleting,
  });

  const removeQueries = useCallback(() => {
    queryClient.removeQueries({ queryKey: flowQueryKeys });
  }, [queryClient]);

  return {
    ...res,
    flows: res.data?.flows || emptyArray,
    promotions: (res.data?.promotions || emptyArray) as IEntityPromotion<FlowID>[],
    total,
    tags: keys,
    refetch,
    mutateUpdatePromotions,
    mutateDeletedFlow,
    removeQueries,
  };
};
